import type React from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import FormDialog from "./FormDialog";
import { useFormDialog } from "../../hooks/useFormDialog";

type TranslationKeys = keyof typeof translations.en;

interface CreateNameFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string }) => Promise<void>;
  error: string | null;
  initialData?: { name: string };
  isEditing?: boolean;
  entityName: string; // e.g., "Location", "Vendor"
  labelTranslationKey: string; // e.g., "Location Name", "Vendor Name"
  placeholder?: string;
  addTextTranslationKey?: string; // e.g., "Add Location", "Add Vendor"
  editTextTranslationKey?: string; // e.g., "Edit Location", "Edit Vendor"
  formTestId?: string;
}

const CreateNameForm: React.FC<CreateNameFormProps> = ({
  open,
  onClose,
  onSubmit,
  error,
  initialData = { name: "" },
  isEditing = false,
  entityName,
  labelTranslationKey,
  placeholder,
  addTextTranslationKey,
  editTextTranslationKey,
  formTestId,
}) => {
  const { t } = useTranslation();

  const { loading, formData, validationErrors, handleChange, handleSubmit, handleClose } =
    useFormDialog({
      initialData,
      onSubmit,
      onClose,
      validate: (data) => {
        const errors: string[] = [];
        if (!data.name.trim()) {
          errors.push(`${entityName} name is required`);
        }
        return errors;
      },
    });

  const title = isEditing
    ? editTextTranslationKey
      ? t(editTextTranslationKey as TranslationKeys)
      : `Edit ${entityName}`
    : addTextTranslationKey
      ? t(addTextTranslationKey as TranslationKeys)
      : `Add ${entityName}`;

  const loadingMessage = isEditing
    ? `Updating ${entityName.toLowerCase()}...`
    : `Creating ${entityName.toLowerCase()}...`;

  const submitText = isEditing
    ? "Save Changes"
    : addTextTranslationKey
      ? t(addTextTranslationKey as TranslationKeys)
      : `Add ${entityName}`;

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={title}
      loading={loading}
      loadingMessage={loadingMessage}
      error={error}
      validationErrors={validationErrors}
      submitDisabled={!formData.name.trim()}
      submitText={submitText}
      dialogTestId={formTestId}
    >
      <TextField
        autoFocus
        margin="dense"
        label={t(labelTranslationKey as TranslationKeys)}
        placeholder={placeholder || `${entityName} name`}
        name="name"
        type="text"
        fullWidth
        value={formData.name}
        onChange={handleChange}
        required
        disabled={loading}
        sx={{ mb: 2 }}
      />
    </FormDialog>
  );
};

export default CreateNameForm;
